import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export interface SelectableCardProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Currently selected state — drives active styling. */
  selected?: boolean;
  /** Card content — typically a label or a title + description. */
  children: ReactNode;
}

const baseStyles =
  'w-full text-left px-4 py-3.5 rounded-lg border-2 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed';

const selectedStyles = 'border-accent bg-accent/5 text-primary font-medium';
const idleStyles = 'border-neutral-200 text-neutral-600 hover:border-neutral-300';

/**
 * Pressable option card — border-2 wrapper with an accent-tinted active
 * state. Used for single-choice lists (trip scope, travel level, party
 * type) where a plain radio looks too dense.
 *
 * This is a button under the hood — it fires `onClick` on keyboard enter
 * and click alike. For RadioGroup semantics, use the `Radio` primitive.
 */
export const SelectableCard = forwardRef<HTMLButtonElement, SelectableCardProps>(
  function SelectableCard(props, ref) {
    const {
      selected = false,
      className = '',
      type = 'button',
      children,
      ...rest
    } = props;

    const cls = cn(baseStyles, selected ? selectedStyles : idleStyles, className);

    return (
      <button
        ref={ref}
        type={type}
        aria-pressed={selected}
        className={cls}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
